/**
 * Creature sync (device-first): the device keeps every creature (metadata + PNG) and works offline; signed-in
 * families also keep a cloud copy so creatures follow the child to a new device. Merge rules: union by id; the
 * same creature keeps the most played-with copy (max `plays`) and the newest name; deleted ids (tombstones) never
 * come back; newest first, at most `MAX_CREATURES_PER_PROFILE` per profile. Pure, used by client and server.
 */
import { MAX_CREATURES_PER_PROFILE } from './collection';
import type { CreatureMeta } from './types';

export interface CreatureSyncPayload {
  creatures: CreatureMeta[];
  /** Ids deleted on this device (tombstones). */
  removed?: string[];
}

/** Merge two lists of ONE profile's creatures. */
export function mergeCreatureLists(a: readonly CreatureMeta[], b: readonly CreatureMeta[], removed: readonly string[] = [], max = MAX_CREATURES_PER_PROFILE): CreatureMeta[] {
  const gone = new Set(removed);
  const byId = new Map<string, CreatureMeta>();
  for (const c of [...a, ...b]) {
    if (gone.has(c.id)) continue;
    const prev = byId.get(c.id);
    if (!prev) {
      byId.set(c.id, c);
      continue;
    }
    const more = (c.plays ?? 0) > (prev.plays ?? 0) ? c : prev;
    byId.set(c.id, { ...prev, ...more, plays: Math.max(prev.plays ?? 0, c.plays ?? 0), name: c.name || prev.name });
  }
  return [...byId.values()].sort((x, y) => y.createdAt.localeCompare(x.createdAt)).slice(0, max);
}

/** Basic shape check of an incoming creature (server side): drops anything malformed. */
export function isCreatureMeta(v: unknown): v is CreatureMeta {
  const c = v as Partial<CreatureMeta> | null;
  return (
    !!c &&
    typeof c.id === 'string' && c.id.length > 0 && c.id.length <= 64 &&
    typeof c.name === 'string' && c.name.length <= 40 &&
    typeof c.worldId === 'string' &&
    typeof c.subject === 'string' && c.subject.length <= 80 &&
    typeof c.createdAt === 'string' && !Number.isNaN(Date.parse(c.createdAt)) &&
    typeof c.width === 'number' && typeof c.height === 'number' &&
    Array.isArray(c.eyes) && c.eyes.length <= 4 &&
    typeof c.kind === 'string' && typeof c.accent === 'string' && c.accent.length <= 16
  );
}
